import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  resolveNotificationTab,
} from './notificationsApi';
import { onMessageListener } from '../../../firebase';
import { TabContext } from '../../Context/TabContext';

const NOTIF_PARAMS = ['notifIdObjekta', 'notifIzvor', 'notifPoruka'];

const readNotificationFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const idObjekta = params.get('notifIdObjekta');
  if (!idObjekta) return null;

  const notifikacija = {
    idObjekta,
    izvorTabela: params.get('notifIzvor'),
    poruka: params.get('notifPoruka') || '',
  };

  NOTIF_PARAMS.forEach((key) => params.delete(key));
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}`,
  );

  return notifikacija;
};

/**
 * Shared notifications state + realtime refresh (FCM foreground, visibility, SW).
 * Klik na notifikaciju sa detaljima otvara odgovarajući tab.
 */
const useNotifications = () => {
  const { tabs, addTab } = useContext(TabContext);
  const [userNotifications, setUserNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const unreadOnlyRef = useRef(unreadOnly);
  unreadOnlyRef.current = unreadOnly;

  const openTabFromPush = useCallback((payload) => {
    const tab = resolveNotificationTab({
      idObjekta: payload?.idObjekta,
      izvorTabela: payload?.izvorTabela,
      poruka: payload?.poruka,
    });
    if (tab) setPendingTab(tab);
  }, []);

  // Ceka da TabContext ucita tabove iz localStorage (onPageLoad),
  // inace bi addTab pregazio sacuvane tabove
  useEffect(() => {
    if (!pendingTab || !tabs?.length) return;
    addTab(pendingTab);
    setPendingTab(null);
  }, [pendingTab, tabs, addTab]);

  useEffect(() => {
    const fromUrl = readNotificationFromUrl();
    if (fromUrl) openTabFromPush(fromUrl);
  }, [openTabFromPush]);

  const loadNotifications = useCallback(
    async (unreadFilter = unreadOnly) => {
      const [list, unreadCount] = await Promise.all([
        fetchNotifications({
          unreadOnly: unreadFilter,
          offset: 0,
          next: 50,
        }),
        fetchUnreadCount(),
      ]);
      setUserNotifications(list);
      setUnreadNotifications(unreadCount);
    },
    [unreadOnly],
  );

  const refreshNotifications = useCallback(() => {
    loadNotifications(unreadOnlyRef.current);
  }, [loadNotifications]);

  useEffect(() => {
    loadNotifications(unreadOnly);
  }, [loadNotifications, unreadOnly]);

  useEffect(() => {
    try {
      onMessageListener(() => {
        refreshNotifications();
      });
    } catch (error) {
      console.warn('onMessageListener error:', error);
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshNotifications();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    const onSwMessage = (event) => {
      if (event?.data?.type === 'NEW_NOTIFICATION') {
        refreshNotifications();
      }
      if (event?.data?.type === 'OPEN_NOTIFICATION') {
        openTabFromPush(event.data.payload);
        refreshNotifications();
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', onSwMessage);
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', onSwMessage);
      }
    };
  }, [refreshNotifications, openTabFromPush]);

  const handleUnreadFilterChange = useCallback((value) => {
    setUnreadOnly(Boolean(value));
  }, []);

  const handleReadAllNotifications = useCallback(async () => {
    const ok = await markAllNotificationsAsRead();
    if (!ok) return;

    setUnreadNotifications(0);
    if (unreadOnly) {
      setUserNotifications([]);
    } else {
      setUserNotifications((prev) =>
        prev.map((item) => ({ ...item, read: true })),
      );
    }
  }, [unreadOnly]);

  const handleReadNotification = useCallback(
    async (notification) => {
      if (!notification.read) {
        const ok = await markNotificationAsRead(notification.id);
        if (ok) {
          setUserNotifications((prev) => {
            if (unreadOnly) {
              return prev.filter((item) => item.id !== notification.id);
            }
            return prev.map((item) =>
              item.id === notification.id ? { ...item, read: true } : item,
            );
          });
          setUnreadNotifications((prev) => Math.max(prev - 1, 0));

          const unreadCount = await fetchUnreadCount();
          setUnreadNotifications(unreadCount);
        }
      }
    },
    [unreadOnly],
  );

  const handleOpenNotification = useCallback(
    (notification) => {
      handleReadNotification(notification);
      if (notification.pageType) {
        addTab({
          title: notification.pageTitle,
          icon: notification.pageIcon,
          type: notification.pageType,
          data: notification.pageData,
        });
      }
    },
    [handleReadNotification, addTab],
  );

  return {
    userNotifications,
    unreadNotifications,
    unreadOnly,
    handleUnreadFilterChange,
    handleReadNotification,
    handleOpenNotification,
    handleReadAllNotifications,
    refreshNotifications,
  };
};

export default useNotifications;
